import { useEffect, useRef } from "react";
import { WS_BASE } from "@/lib/api";

/**
 * Live refresh for dashboards & public trackers: interval polling plus a WebSocket
 * subscription to /ws/dispatch that triggers a debounced reload on any event.
 * `org` scopes the socket to a tenant (public demo pages pass org_demo_sample).
 */
const IGNORED = new Set(["truck.position", "truck.location", "hello", "ping"]);

export default function useLiveRefresh(reload, { intervalMs = 30_000, org = null, enabled = true, minGapMs = 1500 } = {}) {
  const fnRef = useRef(reload);
  fnRef.current = reload;

  useEffect(() => {
    if (!enabled) return undefined;
    let closed = false;
    let ws = null;
    let retry = 0;
    let timer = null;
    let debounce = null;
    let last = 0;
    const fire = () => {
      clearTimeout(debounce);
      const wait = Math.max(250, minGapMs - (Date.now() - last));
      debounce = setTimeout(() => { last = Date.now(); fnRef.current?.(); }, wait);
    };
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${WS_BASE}/ws/dispatch${org ? `?org=${encodeURIComponent(org)}` : ""}`);
      } catch {
        return;
      }
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (IGNORED.has(evt.type)) return;
          fire();
        } catch { /* ignore */ }
      };
      ws.onopen = () => { retry = 0; };
      ws.onclose = () => {
        if (closed) return;
        retry += 1;
        timer = setTimeout(connect, Math.min(15_000, 1000 * 2 ** Math.min(retry, 4)));
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    const poll = setInterval(() => fnRef.current?.(), intervalMs);
    return () => {
      closed = true;
      clearInterval(poll);
      clearTimeout(timer);
      clearTimeout(debounce);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [intervalMs, org, enabled]);
}
